import React, { useState, useRef, useEffect } from 'react';

const S = {
  overlay: {
    background: '#0a0a0c',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px 60px',
    fontFamily: "'Jost', 'Outfit', sans-serif",
    color: '#f0ede6',
  },
  eyebrow: {
    fontSize: '11px',
    letterSpacing: '0.16em',
    color: '#c9a84c',
    textTransform: 'uppercase',
    marginBottom: '12px',
    fontWeight: 500,
  },
  headline: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '36px',
    fontWeight: 500,
    color: '#f0ede6',
    textAlign: 'center',
    marginBottom: '8px',
  },
  sub: {
    fontSize: '14px',
    color: 'rgba(240,237,230,0.5)',
    textAlign: 'center',
    marginBottom: '40px',
  },
  letterWrap: {
    maxWidth: '680px',
    width: '100%',
    background: '#111114',
    border: '0.5px solid rgba(201,168,76,0.2)',
    borderRadius: '14px',
    padding: '44px 48px',
    position: 'relative',
    overflow: 'hidden',
  },
  cornerAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '60px',
    height: '60px',
    borderTop: '1.5px solid rgba(201,168,76,0.35)',
    borderLeft: '1.5px solid rgba(201,168,76,0.35)',
    borderRadius: '14px 0 0 0',
    pointerEvents: 'none',
  },
  cornerAccentBR: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '60px',
    height: '60px',
    borderBottom: '1.5px solid rgba(201,168,76,0.35)',
    borderRight: '1.5px solid rgba(201,168,76,0.35)',
    borderRadius: '0 0 14px 0',
    pointerEvents: 'none',
  },
  symbol: {
    fontSize: '28px',
    color: '#c9a84c',
    marginBottom: '20px',
    display: 'block',
    fontFamily: "'Cormorant Garamond', serif",
  },
  greeting: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px',
    fontWeight: 500,
    color: '#f0ede6',
    marginBottom: '24px',
  },
  body: {
    fontSize: '15px',
    lineHeight: '1.85',
    color: 'rgba(240,237,230,0.85)',
    marginBottom: '20px',
  },
  pullQuote: {
    borderLeft: '2px solid #c9a84c',
    paddingLeft: '20px',
    margin: '28px 0',
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '20px',
    fontStyle: 'italic',
    color: '#e8c97a',
    lineHeight: '1.6',
  },
  signatureWrap: {
    marginTop: '36px',
    paddingTop: '28px',
    borderTop: '0.5px solid rgba(201,168,76,0.15)',
  },
  signatureName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '28px',
    fontStyle: 'italic',
    color: '#c9a84c',
    marginBottom: '4px',
  },
  signatureTitle: {
    fontSize: '12px',
    letterSpacing: '0.1em',
    color: 'rgba(240,237,230,0.4)',
    textTransform: 'uppercase',
  },
  scrollHint: {
    maxWidth: '680px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 0 0',
    fontSize: '12px',
    color: 'rgba(240,237,230,0.35)',
    letterSpacing: '0.06em',
  },
  btnWrap: {
    maxWidth: '680px',
    width: '100%',
    marginTop: '24px',
  },
  btn: (active) => ({
    width: '100%',
    padding: '16px',
    background: active ? 'rgba(201,168,76,0.12)' : 'rgba(240,237,230,0.04)',
    border: `1px solid ${active ? 'rgba(201,168,76,0.5)' : 'rgba(240,237,230,0.1)'}`,
    borderRadius: '10px',
    color: active ? '#e8c97a' : 'rgba(240,237,230,0.3)',
    fontFamily: "'Jost', sans-serif",
    fontSize: '14px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: active ? 'pointer' : 'not-allowed',
    transition: 'all 0.3s ease',
    fontWeight: 500,
  }),
};

export default function LynnIntroLetter({ onComplete, studentName = 'Trader' }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [completed, setCompleted] = useState(false);
  const letterRef = useRef(null);

  useEffect(() => {
    const el = letterRef.current;
    if (!el) return;
    const check = () => {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
      if (atBottom) setScrolledToBottom(true);
    };
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, []);

  const handleComplete = () => {
    if (!scrolledToBottom) return;
    setCompleted(true);
    setTimeout(() => onComplete && onComplete(), 600);
  };

  return (
    <div style={S.overlay}>
      <div style={S.eyebrow}>♀ A letter from your mentor</div>
      <h1 style={S.headline}>Before We Begin</h1>
      <p style={S.sub}>Read Lynn's letter, then start your journey.</p>

      <div
        ref={letterRef}
        style={{ ...S.letterWrap, maxHeight: '60vh', overflowY: 'auto' }}
      >
        <div style={S.cornerAccent} />
        <div style={S.cornerAccentBR} />

        <span style={S.symbol}>♀</span>

        <div style={S.greeting}>Dear {studentName},</div>

        <p style={S.body}>
          I want to start by telling you something nobody told me: you don't need a finance degree,
          a wealthy family, or a Wall Street connection to build real financial freedom. You need
          patience, a plan, and the willingness to learn something that most people never will.
        </p>

        <p style={S.body}>
          My name is Lynn. In 2020, I was a professional photographer who had just survived financial
          abuse. I didn't have much left — but I had time, curiosity, and a deep need to never
          depend on anyone else for my financial security again. I found options trading almost
          by accident, and it changed everything.
        </p>

        <div style={S.pullQuote}>
          "I turned a small account into a 304% gain over four years — not by gambling,
          but by selling options patiently, strategically, and profitably."
        </div>

        <p style={S.body}>
          I didn't do it by chasing hot stocks or watching charts all day. I did it by becoming
          a seller — collecting premium the way an insurance company collects premiums — and letting
          the math and time work in my favor. Ninety-nine percent of my profits come from selling
          options, not buying them. That's what this program is about.
        </p>

        <p style={S.body}>
          This is not a get-rich-quick course. I won't promise you'll make a certain amount of
          money. What I will promise is that if you work through this material honestly — if you
          paper trade before you risk real money, if you journal your decisions, if you take the
          quizzes seriously — you will understand markets in a way that most investors never do.
        </p>

        <p style={S.body}>
          I built Trade Like a Woman because I noticed something: the data consistently shows
          that women outperform men as investors. We're patient. We don't overtrade. We follow
          rules. We don't let our ego make our decisions. Those are exactly the traits of a
          successful option seller — and you already have them.
        </p>

        <div style={S.pullQuote}>
          "The market will be here every single day. There is never a reason to rush."
        </div>

        <p style={S.body}>
          You'll move through ten chapters at your own pace. Each one builds on the last.
          You'll need to score 75% or higher on each quiz to unlock the next chapter — not to
          gatekeep you, but because each strategy depends on understanding the one before it.
          You'll paper trade for at least 30 days before you ever risk real money. This is
          non-negotiable, and it's the part most people want to skip. Please don't.
        </p>

        <p style={S.body}>
          I lost $75,000 in a single day early in my trading journey. I wanted to quit. I
          almost did. I'm telling you this now so you know: I have sat exactly where you might
          sit someday — terrified, doubting everything. And I kept going. The strategies in
          this program are the ones I came back to, refined, and trade to this day.
        </p>

        <p style={S.body}>
          You were accepted into this program because I believe you're ready to do this the
          right way. I'm honored to be your guide. Now — let's get to work.
        </p>

        <div style={S.signatureWrap}>
          <div style={S.signatureName}>Lynn</div>
          <div style={S.signatureTitle}>Founder · Trade Like a Woman</div>
        </div>
      </div>

      <div style={S.scrollHint}>
        {scrolledToBottom
          ? "✓ You've read Lynn's letter — you're ready to begin."
          : '↓ Scroll to the bottom of the letter to continue'}
      </div>

      <div style={S.btnWrap}>
        <button style={S.btn(scrolledToBottom)} onClick={handleComplete} disabled={!scrolledToBottom}>
          {completed ? '✓ Beginning Your Journey...' : 'Begin My Journey →'}
        </button>
      </div>
    </div>
  );
}
